import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from 'lib/utils'
import { transformUrl, TRANSFORM } from 'components/FileUploader/fileUploadUtils'

interface NoteImageCarouselProps {
	urls: string[]
	/** Fired with the index of the clicked image. */
	onSelect: (index: number) => void
	className?: string
}

/**
 * A note's images, one at a time.
 *
 * Replaces the collage this card used to draw. A collage gives every image a
 * different size and crop, so a four-image note showed four thumbnails and a
 * single-image note showed one big picture: the same content read differently
 * depending on how much of it there was. One full-width frame per image treats
 * them all alike, and at this column width a quarter-tile was too small to
 * judge a site photograph by anyway.
 *
 * Built on native scroll-snap rather than a carousel library, so trackpad,
 * touch and keyboard scrolling keep working for free. Each slide is exactly one
 * page wide, which is what makes the arrows step one image and the counter
 * agree with what is on screen.
 */
const NoteImageCarousel: React.FC<NoteImageCarouselProps> = ({ urls, onSelect, className }) => {
	const trackRef = useRef<HTMLDivElement>(null)
	const [index, setIndex] = useState(0)

	const syncIndex = useCallback(() => {
		const el = trackRef.current
		if (!el || el.clientWidth === 0) return
		// Round rather than floor: a snap point settles a fraction of a pixel
		// off, which would otherwise read as still being on the previous image.
		setIndex(Math.round(el.scrollLeft / el.clientWidth))
	}, [])

	useEffect(() => {
		const el = trackRef.current
		if (!el) return
		el.addEventListener('scroll', syncIndex, { passive: true })

		// The track starts at zero width inside a freshly mounted card, so the
		// position must be recomputed once it has been laid out.
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncIndex) : null
		observer?.observe(el)

		return () => {
			el.removeEventListener('scroll', syncIndex)
			observer?.disconnect()
		}
	}, [syncIndex, urls.length])

	const step = (direction: 1 | -1) => {
		const el = trackRef.current
		if (!el) return
		el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' })
	}

	if (urls.length === 0) return null

	const isSingle = urls.length === 1
	const atStart = index <= 0
	const atEnd = index >= urls.length - 1

	return (
		<div className={cn('relative', className)} data-testid="note-image-carousel">
			<div
				ref={trackRef}
				className={cn(
					'flex overflow-x-auto',
					// Hide the scrollbar chrome; the arrows and the counter carry
					// the affordance.
					'[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
					!isSingle && 'snap-x snap-mandatory',
				)}
			>
				{urls.map((url, slide) => (
					<button
						key={`${url}-${slide}`}
						type="button"
						onClick={() => onSelect(slide)}
						aria-label={`Open image ${slide + 1} of ${urls.length}`}
						data-testid="note-carousel-image"
						className="relative h-96 w-full shrink-0 snap-start overflow-hidden border-0 bg-muted p-0"
					>
						<img
							src={transformUrl(url, TRANSFORM.PREVIEW)}
							alt={`Note attachment ${slide + 1}`}
							loading="lazy"
							className="h-full w-full object-cover transition-opacity hover:opacity-90"
						/>
					</button>
				))}
			</div>

			{!isSingle && (
				<>
					<button
						type="button"
						onClick={() => step(-1)}
						disabled={atStart}
						aria-label="Previous image"
						className={cn(
							'absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-1.5 text-white transition-opacity',
							atStart ? 'pointer-events-none opacity-0' : 'opacity-100 hover:bg-black/70',
						)}
					>
						<ChevronLeft className="h-5 w-5" />
					</button>
					<button
						type="button"
						onClick={() => step(1)}
						disabled={atEnd}
						aria-label="Next image"
						className={cn(
							'absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-1.5 text-white transition-opacity',
							atEnd ? 'pointer-events-none opacity-0' : 'opacity-100 hover:bg-black/70',
						)}
					>
						<ChevronRight className="h-5 w-5" />
					</button>

					{/* Position as well as total: with one frame on screen the count
					    alone would not say where in the set the reader is. */}
					<div
						data-testid="note-carousel-count"
						className="pointer-events-none absolute bottom-2 right-3 rounded-full bg-black/55 px-2 py-0.5 text-xs tabular-nums text-white"
					>
						{Math.min(index + 1, urls.length)} / {urls.length}
					</div>
				</>
			)}
		</div>
	)
}

export default NoteImageCarousel
